import React, { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  BarChart3,
  Bell,
  CalendarClock,
  ChartLine,
  CheckSquare2,
  Bookmark,
  DoorOpen,
  FileText,
  HelpCircle,
  Home,
  MessageCircle,
  MessagesSquare,
  Settings,
  UserRound,
  Users,
  Mail,
} from "lucide-react";

const DashboardLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  const isActive = (path: string) => location.pathname === path;

  const getNavigationItems = () => {
    if (user?.type === "customer") {
      return [
        { path: "/dashboard/customer", label: "Dashboard", icon: <Home className="size-5" /> },
        { path: "/dashboard/customer/profile", label: "Profile", icon: <UserRound className="size-5" /> },
        {
          path: "/dashboard/customer/saved-properties",
          label: "Saved Properties",
          icon: <Bookmark className="size-5" />,
        },
        {
          path: "/dashboard/customer/bookings",
          label: "My Bookings",
          icon: <CalendarClock className="size-5" />,
        },
        {
          path: "/dashboard/customer/my-agent",
          label: "My Agent",
          icon: <Users className="size-5" />,
        },
        {
          path: "/dashboard/customer/messages",
          label: "Messages",
          icon: <MessageCircle className="size-5" />,
        },
        {
          path: "/dashboard/customer/documents",
          label: "Documents",
          icon: <FileText className="size-5" />,
        },
        {
          path: "/dashboard/customer/help",
          label: "Help & Support",
          icon: <HelpCircle className="size-5" />,
        },
      ];
    } else if (user?.type === "agent") {
      return [
        { path: "/dashboard/agent", label: "Overview", icon: <BarChart3 className="size-5" /> },
        { path: "/dashboard/agent/leads", label: "Leads", icon: <Users className="size-5" /> },
        { path: "/dashboard/agent/bookings", label: "Bookings", icon: <CalendarClock className="size-5" /> },
        {
          path: "/dashboard/agent/availability",
          label: "Availability",
          icon: <Bell className="size-5" />,
        },
        { path: "/dashboard/agent/messages", label: "Messages", icon: <MessagesSquare className="size-5" /> },
        { path: "/dashboard/agent/communication", label: "Communication", icon: <Mail className="size-5" /> },
        { path: "/dashboard/agent/tasks", label: "Tasks", icon: <CheckSquare2 className="size-5" /> },
      ];
    } else if (user?.type === "admin") {
      return [
        { path: "/dashboard/admin", label: "Manage Users", icon: <Users className="size-5" /> },
        {
          path: "/dashboard/admin/properties",
          label: "Manage Properties",
          icon: <Home className="size-5" />,
        },
        { path: "/dashboard/admin/tours", label: "Manage Tours", icon: <CalendarClock className="size-5" /> },
        {
          path: "/dashboard/admin/team-chat",
          label: "Manage Team Chat",
          icon: <MessagesSquare className="size-5" />,
        },
        { path: "/dashboard/admin/alerts", label: "Manage Alerts", icon: <Bell className="size-5" /> },
        {
          path: "/dashboard/admin/settings",
          label: "System Settings",
          icon: <Settings className="size-5" />,
        },
        {
          path: "/dashboard/admin/analytics",
          label: "Analytics & Reports",
          icon: <ChartLine className="size-5" />,
        },
      ];
    }
    return [];
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="min-h-screen bg-gray-50 dashboard-theme">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 bg-white shadow-lg text-gray-600 hover:text-primary"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 py-2 px-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="text-xl font-bold text-primary">
                Alkina Realty
              </span>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Avatar className="w-10 h-10">
                <AvatarImage
                  src={user?.avatar}
                  alt={`${user?.firstName} ${user?.lastName}`}
                />
                <AvatarFallback className="bg-gray-300 text-gray-700 text-sm font-medium">
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.type}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-3 py-2 text-sm font-medium transition-colors ${isActive(item.path)
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg text-current">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <Link
              to="/"
              className="flex items-center space-x-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <DoorOpen className="size-5" />
              <span>Back to Site</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ml-64">
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;

